import React, { useState, useEffect } from 'react';
import {
  productService,
  Product,
  CreateProductData,
  ProductWithBulkRequirements,
  ProductBulkRequirement,
} from '../services/productService';
import { bulkProductService, BulkProduct } from '../services/bulkProductService';
import { productionLineService } from '../services/productionLineService';

const Products: React.FC = () => {
  const [products, setProducts] = useState<ProductWithBulkRequirements[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [modalError, setModalError] = useState(''); // Error state for modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingProduct, setConvertingProduct] = useState<Product | null>(null);
  const [convertFormData, setConvertFormData] = useState({
    shelf_life_days: '',
    storage_requirements: 'Warehouse' as 'Honeyware' | 'Cool Room' | 'Warehouse' | 'Chocolate room',
    packing_speed_units: '',
    packing_speed_time: '',
    packing_speed_unit: 'seconds' as 'seconds' | 'minutes' | 'hours',
    production_line_id: '',
    units_per_case: '',
  });
  const [convertError, setConvertError] = useState('');
  const [convertLoading, setConvertLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Request cancellation
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [formData, setFormData] = useState<CreateProductData>({
    name: '',
    product_code: '',
    contains_nuts: false,
    sticky_ingredients: false,
    shelf_life_days: 365,
    storage_requirements: 'Warehouse',
    units_per_case: 12,
    packing_speed_units: 1,
    packing_speed_time: 30,
    packing_speed_unit: 'seconds',
    label_requirements: '',
    auto_inventory_threshold: 10,
    auto_inventory_ideal: 50,
    category: '',
    brand: '',
    box_size: '',
    container_name: '',
    production_line_id: undefined,
  });
  const [casesPerHour, setCasesPerHour] = useState<number>(120);
  const [machineCasesPerHour, setMachineCasesPerHour] = useState<number | null>(null);

  // Bulk requirements state
  const [availableBulkProducts, setAvailableBulkProducts] = useState<BulkProduct[]>([]);
  const [bulkRequirements, setBulkRequirements] = useState<ProductBulkRequirement[]>([]);
  const [bulkProductSearch, setBulkProductSearch] = useState('');
  const [selectedBulkProduct, setSelectedBulkProduct] = useState<number | null>(null);
  const [bulkQuantity, setBulkQuantity] = useState<number>(1);
  const [finishedQuantity, setFinishedQuantity] = useState<number>(1);

  // New fields dropdown options
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [boxSizes, setBoxSizes] = useState<string[]>([]);
  const [containerNames, setContainerNames] = useState<string[]>([]);
  const [productionLines, setProductionLines] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    // Load initial state from URL on component mount
    const url = new URL(window.location.href);
    let urlPage = parseInt(url.searchParams.get('page') || '1');
    const urlPageSize = parseInt(url.searchParams.get('pageSize') || '20');
    const urlSearch = url.searchParams.get('search') || '';

    // Validate page number (will be clamped after data loads)
    if (urlPage < 1) urlPage = 1;

    setCurrentPage(urlPage);
    setPageSize(urlPageSize);
    setSearchTerm(urlSearch);
    setDebouncedSearchTerm(urlSearch);

    // Load dropdown options
    loadDropdownOptions();
  }, []);

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search term changes
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    // Load data when pagination parameters change
    if (currentPage > 0 && pageSize > 0) {
      loadData();
    }
  }, [currentPage, pageSize, debouncedSearchTerm, showAllProducts]);

  useEffect(() => {
    // Update URL with current page and pageSize
    if (currentPage > 0 && pageSize > 0) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', currentPage.toString());
      url.searchParams.set('pageSize', pageSize.toString());
      if (searchTerm) {
        url.searchParams.set('search', searchTerm);
      } else {
        url.searchParams.delete('search');
      }
      window.history.replaceState({}, '', url.toString());
    }
  }, [currentPage, pageSize, searchTerm]);

  const loadData = async (page = currentPage, size = pageSize) => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setLoading(true);
      setError('');

      let result;
      if (debouncedSearchTerm.trim()) {
        result = await productService.searchProductsPaginated(
          debouncedSearchTerm,
          page,
          size,
          true,
          showAllProducts
        );
      } else {
        result = await productService.getProductsPaginated(page, size, true, showAllProducts);
      }

      // Check if request was cancelled
      if (abortController.signal.aborted) {
        return;
      }

      setProducts(result.products as ProductWithBulkRequirements[]);
      const newTotalPages = result.pagination.totalPages;
      setTotalPages(newTotalPages);
      setTotalItems(result.pagination.total);

      // Validate and clamp currentPage to valid range
      if (page > newTotalPages && newTotalPages > 0) {
        setCurrentPage(1);
      } else if (page < 1) {
        setCurrentPage(1);
      }
    } catch (err: any) {
      // Don't set error if request was cancelled
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      setError(err.message);
    } finally {
      // Only update loading state if this request wasn't cancelled
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const loadDropdownOptions = async () => {
    try {
      const [categoriesData, brandsData, boxSizesData, containerNamesData, productionLinesData] =
        await Promise.all([
          productService.getUniqueCategories(),
          productService.getUniqueBrands(),
          productService.getUniqueBoxSizes(),
          productService.getUniqueContainerNames(),
          productionLineService.getActiveProductionLines(),
        ]);

      setCategories(categoriesData);
      setBrands(brandsData);
      setBoxSizes(boxSizesData);
      setContainerNames(containerNamesData);
      const mappedLines = productionLinesData.map((line) => ({ id: line.id, name: line.name }));
      setProductionLines(mappedLines);
    } catch (err) {
      console.error('Error loading dropdown options:', err);
    }
  };

  const handleSearch = async () => {
    // Reset to first page when searching
    setCurrentPage(1);
    // Debounced search will trigger loadData automatically
  };

  const handlePageChange = (newPage: number) => {
    // Validate page number before setting
    const validPage = Math.max(1, Math.min(newPage, totalPages || 1));
    setCurrentPage(validPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setModalError('');
      setError('');

      const submitData = {
        ...formData,
        cases_per_hour: casesPerHour,
        machine_cases_per_hour: machineCasesPerHour ?? undefined,
      };

      if (editingProduct) {
        await productService.updateProduct(editingProduct.id, submitData);
      } else {
        await productService.createProduct(submitData);
      }
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      await loadData();
    } catch (err: any) {
      // Show error in modal instead of page-level error
      const errorMessage = err.response?.data?.error?.message || err.message || 'An error occurred';
      setModalError(errorMessage);
    }
  };

  const handleEdit = async (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      product_code: product.product_code,
      contains_nuts: product.contains_nuts,
      sticky_ingredients: product.sticky_ingredients,
      shelf_life_days: product.shelf_life_days ?? 365,
      storage_requirements: product.storage_requirements ?? 'Warehouse',
      units_per_case: product.units_per_case,
      packing_speed_units: product.packing_speed_units ?? 1,
      packing_speed_time: product.packing_speed_time ?? 30,
      packing_speed_unit: product.packing_speed_unit ?? 'seconds',
      label_requirements: product.label_requirements,
      auto_inventory_threshold: product.auto_inventory_threshold ?? 10,
      auto_inventory_ideal: product.auto_inventory_ideal ?? 50,
      auto_create_production_orders: product.auto_create_production_orders !== false,
      category: product.category || '',
      brand: product.brand || '',
      box_size: product.box_size || '',
      container_name: product.container_name || '',
      production_line_id: product.production_line_id ?? undefined,
    });
    // Calculate and set the cases per hour for editing
    const currentCasesPerHour = calculateCasesPerHour(
      product.packing_speed_units ?? 1,
      product.packing_speed_time ?? 30,
      product.packing_speed_unit ?? 'seconds',
      product.units_per_case
    );
    setCasesPerHour(currentCasesPerHour);

    if (
      product.machine_packing_speed_units &&
      product.machine_packing_speed_time &&
      product.machine_packing_speed_unit
    ) {
      const machineSpeed = calculateCasesPerHour(
        product.machine_packing_speed_units,
        product.machine_packing_speed_time,
        product.machine_packing_speed_unit,
        1
      );
      setMachineCasesPerHour(machineSpeed);
    } else {
      setMachineCasesPerHour(null);
    }

    // Load bulk products and requirements
    await loadBulkProducts();
    await loadBulkRequirements(product.id);

    // Ensure production lines are loaded before opening dialog
    if (productionLines.length === 0) {
      await loadDropdownOptions();
    }

    setShowForm(true);
  };

  const handleConvertClick = (product: Product) => {
    setConvertingProduct(product);
    setConvertFormData({
      shelf_life_days: '',
      storage_requirements: 'Warehouse',
      packing_speed_units: '',
      packing_speed_time: '',
      packing_speed_unit: 'seconds',
      production_line_id: '',
      units_per_case: product.units_per_case?.toString() || '1',
    });
    setConvertError('');
    setShowConvertModal(true);
  };

  const handleConvertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!convertingProduct) return;

    setConvertError('');
    setConvertLoading(true);

    try {
      // Validate all required fields
      if (
        !convertFormData.shelf_life_days ||
        !convertFormData.storage_requirements ||
        !convertFormData.packing_speed_units ||
        !convertFormData.packing_speed_time ||
        !convertFormData.packing_speed_unit ||
        !convertFormData.production_line_id
      ) {
        setConvertError('All fields are required');
        setConvertLoading(false);
        return;
      }

      const result = await productService.convertToProduction(convertingProduct.id, {
        shelf_life_days: parseInt(convertFormData.shelf_life_days),
        storage_requirements: convertFormData.storage_requirements,
        packing_speed_units: parseInt(convertFormData.packing_speed_units),
        packing_speed_time: parseFloat(convertFormData.packing_speed_time),
        packing_speed_unit: convertFormData.packing_speed_unit,
        production_line_id: parseInt(convertFormData.production_line_id),
        units_per_case: convertFormData.units_per_case
          ? parseInt(convertFormData.units_per_case)
          : undefined,
      });

      setShowConvertModal(false);
      setConvertingProduct(null);
      await loadData();

      // Show success message
      if (result.message) {
        alert(result.message);
      } else {
        alert(
          'Product successfully converted to production product. Remember to add bulk requirements if needed.'
        );
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error?.details?.join?.('\n') ||
        err.message ||
        'An error occurred while converting the product';
      setConvertError(errorMessage);
    } finally {
      setConvertLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      setError('');
      await productService.deleteProduct(id);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      product_code: '',
      contains_nuts: false,
      sticky_ingredients: false,
      shelf_life_days: 365,
      storage_requirements: 'Warehouse',
      units_per_case: 12,
      packing_speed_units: 1,
      packing_speed_time: 30,
      packing_speed_unit: 'seconds',
      label_requirements: '',
      auto_inventory_threshold: 10,
      auto_inventory_ideal: 50,
      auto_create_production_orders: true,
      category: '',
      brand: '',
      box_size: '',
      container_name: '',
      production_line_id: undefined,
    });
    setCasesPerHour(120);
    setMachineCasesPerHour(null);
    setBulkRequirements([]);
    setBulkProductSearch('');
    setSelectedBulkProduct(null);
    setBulkQuantity(1);
    setFinishedQuantity(1);
    setError('');
    setModalError('');
  };

  // Load bulk products when form opens
  const loadBulkProducts = async () => {
    try {
      const bulkProducts = await bulkProductService.getAllBulkProducts();
      setAvailableBulkProducts(bulkProducts);
    } catch (err: any) {
      console.error('Failed to load bulk products:', err.message);
      setError(`Failed to load bulk products: ${err.message}`);
    }
  };

  // Load bulk requirements for editing product
  const loadBulkRequirements = async (productId: number) => {
    try {
      const productWithBulk = (await productService.getProductById(
        productId,
        true
      )) as ProductWithBulkRequirements;
      setBulkRequirements(productWithBulk.bulk_requirements || []);
    } catch (err: any) {
      console.error('Failed to load bulk requirements:', err.message);
    }
  };

  // Add bulk requirement
  const handleAddBulkRequirement = async () => {
    if (!selectedBulkProduct || bulkQuantity <= 0 || finishedQuantity <= 0) return;

    const bulkProduct = availableBulkProducts.find((bp) => bp.id === selectedBulkProduct);
    if (!bulkProduct) return;

    // Check if already exists
    const existingIndex = bulkRequirements.findIndex(
      (br) => br.bulk_product_id === selectedBulkProduct
    );
    if (existingIndex >= 0) {
      // Update existing
      const updated = [...bulkRequirements];
      updated[existingIndex] = {
        ...updated[existingIndex],
        bulk_quantity: bulkQuantity,
        finished_quantity: finishedQuantity,
        cases_needed_per_case: bulkQuantity, // For backward compatibility
      };
      setBulkRequirements(updated);
    } else {
      // Add new
      const newRequirement: ProductBulkRequirement = {
        id: 0, // Will be set by backend
        product_id: editingProduct?.id || 0,
        bulk_product_id: selectedBulkProduct,
        bulk_quantity: bulkQuantity,
        finished_quantity: finishedQuantity,
        bulk_product_name: bulkProduct.name,
        bulk_product_sku: bulkProduct.sku_number,
        cases_needed_per_case: bulkQuantity, // For backward compatibility
      };
      setBulkRequirements([...bulkRequirements, newRequirement]);
    }

    // If editing existing product, save immediately
    if (editingProduct) {
      try {
        await productService.addBulkRequirement(
          editingProduct.id,
          selectedBulkProduct,
          bulkQuantity,
          finishedQuantity
        );
      } catch (err: any) {
        setError(err.message);
        return;
      }
    }

    // Reset form
    setSelectedBulkProduct(null);
    setBulkQuantity(1);
    setFinishedQuantity(1);
    setBulkProductSearch('');
  };

  // Remove bulk requirement
  const handleRemoveBulkRequirement = async (bulkProductId: number) => {
    setBulkRequirements(bulkRequirements.filter((br) => br.bulk_product_id !== bulkProductId));

    // If editing existing product, remove from backend
    if (editingProduct) {
      try {
        await productService.removeBulkRequirement(editingProduct.id, bulkProductId);
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  // Calculate cases per hour from packing speed data
  const calculateCasesPerHour = (
    units: number,
    time: number,
    unit: string,
    unitsPerCase: number
  ) => {
    // Special case: when unit is 'hours', the value already represents cases per hour (for 6 workers)
    // This is because the database stores cases per hour directly when packing_speed_unit = 'hours'
    if (unit === 'hours') {
      // units already represents cases per hour, just divide by time to normalize
      return Math.round((units / time) * 10) / 10;
    }

    // Convert time to hours
    let timeInHours: number;
    switch (unit) {
      case 'seconds':
        timeInHours = time / 3600;
        break;
      case 'minutes':
        timeInHours = time / 60;
        break;
      default:
        timeInHours = time / 3600;
    }

    // For seconds and minutes, units represents individual units, not cases
    // Calculate cases per hour
    const casesPerTimeUnit = units / unitsPerCase;
    const casesPerHour = casesPerTimeUnit / timeInHours;

    return Math.round(casesPerHour * 10) / 10; // Round to 1 decimal place
  };

  const formatPackingSpeedDisplay = (
    units: number,
    time: number,
    unit: string,
    unitsPerCase: number
  ) => {
    const casesPerHour = calculateCasesPerHour(units, time, unit, unitsPerCase);
    return `${casesPerHour} cases/hour`;
  };

  // Convert cases per hour back to packing speed data for form submission
  const convertCasesPerHourToPackingSpeed = (casesPerHour: number, _unitsPerCase: number) => {
    // Store as cases per hour directly (for 6 workers)
    // This matches the format used when packing_speed_unit = 'hours'
    return {
      packing_speed_units: Math.round(casesPerHour),
      packing_speed_time: 1,
      packing_speed_unit: 'hours' as const,
    };
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Products</h1>
        <p className="page-subtitle">Manage finished products and their specifications</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h3>Product Management</h3>
          <button
            className="btn btn-primary"
            onClick={async () => {
              await loadBulkProducts();
              setShowForm(true);
            }}
          >
            Add New Product
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search products, codes, or ingredients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1, padding: '0.5rem' }}
            />
            <button className="btn btn-secondary" onClick={handleSearch}>
              Search
            </button>
            {searchTerm && (
              <button
                className="btn btn-outline"
                onClick={() => {
                  setSearchTerm('');
                  loadData();
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div
            style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={showAllProducts}
                onChange={(e) => setShowAllProducts(e.target.checked)}
              />
              <span>Show All Products (including invoice-only)</span>
            </label>
          </div>
        </div>

        {/* Products List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading products...</div>
        ) : (
          <>
            <div className="table-container">
              <table className="table" style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ fontSize: '0.8rem' }}>
                    <th>Name</th>
                    <th>Product Code</th>
                    <th>Category</th>
                    <th>Brand</th>
                    <th>Properties</th>
                    <th>Shelf Life</th>
                    <th>Case Info</th>
                    <th>Packing Speed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '0.875rem' }}>
                  {!loading && products.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                        No products found
                      </td>
                    </tr>
                  ) : (
                    products.map((product) => (
                      <tr key={product.id}>
                        <td>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <strong>{product.name}</strong>
                              {product.is_for_production === false && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    padding: '0.125rem 0.375rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                  }}
                                  title="Invoice-only product (not for production)"
                                >
                                  Invoice Only
                                </span>
                              )}
                            </div>
                            {product.bulk_requirements.length > 0 && (
                              <div
                                style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}
                              >
                                {product.bulk_requirements.length} ingredient
                                {product.bulk_requirements.length !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{product.product_code}</td>
                        <td>{product.category || '-'}</td>
                        <td>{product.brand || '-'}</td>
                        <td>
                          <div
                            style={{
                              fontSize: '0.875rem',
                              display: 'flex',
                              gap: '0.5rem',
                              flexWrap: 'wrap',
                            }}
                          >
                            {product.contains_nuts && (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: '#f59e0b',
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                }}
                              >
                                Nuts
                              </span>
                            )}
                            {product.sticky_ingredients && (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: '#8b5cf6',
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                }}
                              >
                                Sticky
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{product.shelf_life_days} days</td>
                        <td>{product.units_per_case} units/case</td>
                        <td>
                          {formatPackingSpeedDisplay(
                            product.packing_speed_units ?? 1,
                            product.packing_speed_time ?? 30,
                            product.packing_speed_unit ?? 'seconds',
                            product.units_per_case
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                            {product.is_for_production === false && (
                              <button
                                className="btn btn-small"
                                onClick={() => handleConvertClick(product)}
                                title="Convert to Production Product"
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  backgroundColor: '#3b82f6',
                                  border: '1px solid #2563eb',
                                  borderRadius: '4px',
                                  color: 'white',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease-in-out',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#2563eb';
                                  e.currentTarget.style.borderColor = '#1d4ed8';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#3b82f6';
                                  e.currentTarget.style.borderColor = '#2563eb';
                                }}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M12 2v20M2 12h20" />
                                </svg>
                                Convert
                              </button>
                            )}
                            <button
                              className="btn btn-small"
                              onClick={() => handleEdit(product)}
                              title="Edit Product"
                              style={{
                                padding: '0.375rem 0.75rem',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                color: '#495057',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease-in-out',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                e.currentTarget.style.borderColor = '#adb5bd';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.borderColor = '#dee2e6';
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              className="btn btn-small"
                              onClick={() => handleDelete(product.id)}
                              title="Delete Product"
                              style={{
                                padding: '0.375rem 0.75rem',
                                backgroundColor: '#fff5f5',
                                border: '1px solid #fed7d7',
                                borderRadius: '4px',
                                color: '#c53030',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease-in-out',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#fed7d7';
                                e.currentTarget.style.borderColor = '#fc8181';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#fff5f5';
                                e.currentTarget.style.borderColor = '#fed7d7';
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="3,6 5,6 21,6" />
                                <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '1rem',
                  padding: '1rem 0',
                  borderTop: '1px solid #e1e5e9',
                }}
              >
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Showing {(currentPage - 1) * pageSize + 1} to{' '}
                  {Math.min(currentPage * pageSize, totalItems)} of {totalItems} products
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {/* Page Size Selector */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.875rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                      }}
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Page Navigation */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                        color: currentPage === 1 ? '#9ca3af' : '#374151',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Previous
                    </button>

                    <span style={{ fontSize: '0.875rem', color: '#6b7280', padding: '0 0.5rem' }}>
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white',
                        color: currentPage === totalPages ? '#9ca3af' : '#374151',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              maxWidth: '800px',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
            }}
          >
            <div
              className="modal-header"
              style={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 10,
                borderBottom: '1px solid #e1e5e9',
                borderRadius: '8px 8px 0 0',
              }}
            >
              <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
              <button
                className="btn btn-small"
                onClick={() => {
                  setShowForm(false);
                  setEditingProduct(null);
                  resetForm();
                  setModalError('');
                }}
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
              {modalError && (
                <div
                  style={{
                    margin: '1rem 1.5rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    color: '#c33',
                    position: 'relative',
                    zIndex: 1001,
                  }}
                >
                  {modalError}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                {/* Section 1: Basic Information */}
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '1.25rem',
                    marginBottom: '1rem',
                    borderRadius: '6px',
                    border: '1px solid #e1e5e9',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <h4
                    style={{
                      margin: '0 0 1rem 0',
                      color: '#1a202c',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #e1e5e9',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    Product Details
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label htmlFor="name">Product Name *</label>
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        maxLength={100}
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="product_code">Product Code *</label>
                      <input
                        type="text"
                        id="product_code"
                        value={formData.product_code}
                        onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
                        required
                        maxLength={50}
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="shelf_life_days">Shelf Life (days) *</label>
                      <input
                        type="number"
                        id="shelf_life_days"
                        value={formData.shelf_life_days}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            shelf_life_days: parseInt(e.target.value) || 0,
                          })
                        }
                        required
                        min="1"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="storage_requirements">Storage Requirements *</label>
                      <select
                        id="storage_requirements"
                        value={formData.storage_requirements}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            storage_requirements: e.target.value as
                              | 'Honeyware'
                              | 'Cool Room'
                              | 'Warehouse'
                              | 'Chocolate room',
                          })
                        }
                        required
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                          backgroundColor: 'white',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.75rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1rem',
                          paddingRight: '2.5rem',
                        }}
                      >
                        <option value="Honeyware">Honeyware</option>
                        <option value="Cool Room">Cool Room</option>
                        <option value="Warehouse">Warehouse</option>
                        <option value="Chocolate room">Chocolate room</option>
                      </select>
                    </div>
                  </div>

                  {/* Properties */}
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Product Properties
                    </label>
                    <div style={{ display: 'flex', gap: '2rem' }}>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.contains_nuts}
                          onChange={(e) =>
                            setFormData({ ...formData, contains_nuts: e.target.checked })
                          }
                          style={{ transform: 'scale(1.2)' }}
                        />
                        <span>Contains Nuts</span>
                      </label>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.sticky_ingredients}
                          onChange={(e) =>
                            setFormData({ ...formData, sticky_ingredients: e.target.checked })
                          }
                          style={{ transform: 'scale(1.2)' }}
                        />
                        <span>Sticky Ingredients</span>
                      </label>
                    </div>
                  </div>

                  {/* Label Requirements */}
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label htmlFor="label_requirements">Label Requirements / Notes</label>
                    <textarea
                      id="label_requirements"
                      value={formData.label_requirements}
                      onChange={(e) =>
                        setFormData({ ...formData, label_requirements: e.target.value })
                      }
                      rows={3}
                      style={{
                        padding: '0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '100%',
                        boxSizing: 'border-box',
                        resize: 'vertical',
                      }}
                      placeholder="Enter any special labeling requirements or notes..."
                    />
                  </div>
                </div>

                {/* Section 1.5: Additional Product Information */}
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '1.25rem',
                    marginBottom: '1rem',
                    borderRadius: '6px',
                    border: '1px solid #e1e5e9',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <h4
                    style={{
                      margin: '0 0 1rem 0',
                      color: '#1a202c',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #e1e5e9',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    Additional Information
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label htmlFor="category">Category</label>
                      <input
                        type="text"
                        id="category"
                        list="category-options"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="Enter or select category"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <datalist id="category-options">
                        {categories.map((category, index) => (
                          <option key={index} value={category} />
                        ))}
                      </datalist>
                    </div>
                    <div className="form-group">
                      <label htmlFor="brand">Brand</label>
                      <input
                        type="text"
                        id="brand"
                        list="brand-options"
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        placeholder="Enter or select brand"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <datalist id="brand-options">
                        {brands.map((brand, index) => (
                          <option key={index} value={brand} />
                        ))}
                      </datalist>
                    </div>
                    <div className="form-group">
                      <label htmlFor="box_size">Box Size</label>
                      <input
                        type="text"
                        id="box_size"
                        list="box-size-options"
                        value={formData.box_size}
                        onChange={(e) => setFormData({ ...formData, box_size: e.target.value })}
                        placeholder="Enter or select box size"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <datalist id="box-size-options">
                        {boxSizes.map((boxSize, index) => (
                          <option key={index} value={boxSize} />
                        ))}
                      </datalist>
                    </div>
                    <div className="form-group">
                      <label htmlFor="container_name">Container Name</label>
                      <input
                        type="text"
                        id="container_name"
                        list="container-name-options"
                        value={formData.container_name}
                        onChange={(e) =>
                          setFormData({ ...formData, container_name: e.target.value })
                        }
                        placeholder="Enter or select container name"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <datalist id="container-name-options">
                        {containerNames.map((containerName, index) => (
                          <option key={index} value={containerName} />
                        ))}
                      </datalist>
                    </div>
                    <div className="form-group">
                      <label htmlFor="production_line_id">Production Line *</label>
                      <select
                        id="production_line_id"
                        required
                        value={formData.production_line_id || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            production_line_id: e.target.value
                              ? parseInt(e.target.value)
                              : undefined,
                          })
                        }
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <option value="">Select Production Line</option>
                        {(() => {
                          return productionLines.map((line) => (
                            <option key={line.id} value={line.id}>
                              {line.name}
                            </option>
                          ));
                        })()}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Cases & Packaging */}
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '1.25rem',
                    marginBottom: '1rem',
                    borderRadius: '6px',
                    border: '1px solid #e1e5e9',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <h4
                    style={{
                      margin: '0 0 1rem 0',
                      color: '#1a202c',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #e1e5e9',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    Packaging & Production
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label htmlFor="units_per_case">Units per Case *</label>
                      <input
                        type="number"
                        id="units_per_case"
                        value={formData.units_per_case}
                        onChange={(e) => {
                          const newUnitsPerCase = parseInt(e.target.value) || 0;
                          setFormData({ ...formData, units_per_case: newUnitsPerCase });
                          // Update cases per hour display when units per case changes
                          const currentCasesPerHour = calculateCasesPerHour(
                            formData.packing_speed_units,
                            formData.packing_speed_time,
                            formData.packing_speed_unit,
                            newUnitsPerCase
                          );
                          setCasesPerHour(currentCasesPerHour);
                        }}
                        required
                        min="1"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                        Number of individual units in each case
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="cases_per_hour">Packing Speed (cases/hour/6-workers) *</label>
                      <input
                        type="number"
                        id="cases_per_hour"
                        value={casesPerHour}
                        onChange={(e) => {
                          const newCasesPerHour = parseFloat(e.target.value) || 0;
                          setCasesPerHour(newCasesPerHour);
                          const packingSpeed = convertCasesPerHourToPackingSpeed(
                            newCasesPerHour,
                            formData.units_per_case
                          );
                          setFormData({
                            ...formData,
                            packing_speed_units: packingSpeed.packing_speed_units,
                            packing_speed_time: packingSpeed.packing_speed_time,
                            packing_speed_unit: packingSpeed.packing_speed_unit,
                          });
                        }}
                        required
                        min="0.1"
                        step="0.1"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                        How many cases can be packed per hour
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="machine_cases_per_hour">Machine Speed (cases/hr)</label>
                      <input
                        type="number"
                        id="machine_cases_per_hour"
                        value={machineCasesPerHour ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setMachineCasesPerHour(val === '' ? null : parseFloat(val) || 0);
                        }}
                        min="0.1"
                        step="0.1"
                        placeholder="Optional"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                        Machine packing speed (leave empty if not applicable)
                      </small>
                    </div>
                  </div>
                </div>

                {/* Section 3: Auto Inventory Management */}
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '1.25rem',
                    marginBottom: '1rem',
                    borderRadius: '6px',
                    border: '1px solid #e1e5e9',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <h4
                    style={{
                      margin: '0 0 1rem 0',
                      color: '#1a202c',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #e1e5e9',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    Inventory Management
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label htmlFor="auto_inventory_threshold">Reorder Threshold *</label>
                      <input
                        type="number"
                        id="auto_inventory_threshold"
                        value={formData.auto_inventory_threshold}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            auto_inventory_threshold: parseInt(e.target.value) || 0,
                          })
                        }
                        required
                        min="0"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                        Minimum inventory level before reordering
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="auto_inventory_ideal">Ideal Stock Level *</label>
                      <input
                        type="number"
                        id="auto_inventory_ideal"
                        value={formData.auto_inventory_ideal}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            auto_inventory_ideal: parseInt(e.target.value) || 0,
                          })
                        }
                        required
                        min="0"
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                      <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                        Target inventory level to maintain
                      </small>
                    </div>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.auto_create_production_orders !== false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            auto_create_production_orders: e.target.checked,
                          })
                        }
                        style={{ transform: 'scale(1.2)' }}
                      />
                      <span>Auto Create production orders</span>
                    </label>
                    <small
                      style={{
                        display: 'block',
                        color: '#6c757d',
                        fontSize: '0.875rem',
                        marginTop: '0.25rem',
                      }}
                    >
                      When inventory is low or after invoice deductions, automatically create
                      production orders for this product
                    </small>
                  </div>
                  <div
                    style={{
                      marginTop: '1.5rem',
                      padding: '1rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef',
                      fontSize: '0.875rem',
                      color: '#495057',
                    }}
                  >
                    <strong>Best Practice:</strong> Set the ideal level 2-3 times higher than the
                    threshold to avoid frequent reorders and maintain optimal stock levels.
                  </div>
                </div>

                {/* Section 4: Bulk Requirements */}
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '1.25rem',
                    marginBottom: '1rem',
                    borderRadius: '6px',
                    border: '1px solid #e1e5e9',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <h4
                    style={{
                      margin: '0 0 1rem 0',
                      color: '#1a202c',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #e1e5e9',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    Bulk Ingredients & Raw Materials
                  </h4>

                  {/* Current Bulk Requirements */}
                  {bulkRequirements.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label
                        style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}
                      >
                        Current Ingredients
                      </label>
                      <div
                        style={{
                          border: '1px solid #e1e5e9',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        {bulkRequirements.map((req) => (
                          <div
                            key={req.bulk_product_id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.75rem',
                              borderBottom: '1px solid #f1f3f4',
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                                {req.bulk_product_name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                SKU: {req.bulk_product_sku} • Ratio: {req.bulk_quantity}:
                                {req.finished_quantity}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveBulkRequirement(req.bulk_product_id)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#fff5f5',
                                border: '1px solid #fed7d7',
                                borderRadius: '4px',
                                color: '#c53030',
                                cursor: 'pointer',
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add New Bulk Requirement */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Add Bulk Ingredient
                    </label>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto auto',
                        gap: '0.5rem',
                        alignItems: 'end',
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <label
                          htmlFor="bulk_product_search"
                          style={{
                            fontSize: '0.875rem',
                            marginBottom: '0.25rem',
                            display: 'block',
                          }}
                        >
                          Search Bulk Products
                        </label>
                        <input
                          type="text"
                          id="bulk_product_search"
                          placeholder="Search by name, SKU, or vendor..."
                          value={bulkProductSearch}
                          onChange={async (e) => {
                            setBulkProductSearch(e.target.value);
                            // Perform search if there's a search term
                            if (e.target.value.trim()) {
                              try {
                                const searchResults = await bulkProductService.searchBulkProducts(
                                  e.target.value.trim()
                                );
                                setAvailableBulkProducts(searchResults);
                              } catch (err: any) {
                                console.error('Failed to search bulk products:', err.message);
                                setError(`Failed to search bulk products: ${err.message}`);
                              }
                            } else {
                              // Load all bulk products if search is cleared
                              await loadBulkProducts();
                            }
                          }}
                          onFocus={async () => {
                            // Load bulk products when user focuses on search field
                            if (availableBulkProducts.length === 0) {
                              await loadBulkProducts();
                            }
                          }}
                          onKeyDown={(e) => {
                            // Prevent form submission when Enter is pressed
                            if (e.key === 'Enter') {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            padding: '0.75rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        />
                        {bulkProductSearch &&
                          (availableBulkProducts.length > 0 ? (
                            <div
                              style={{
                                position: 'absolute',
                                zIndex: 1000,
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                marginTop: '0.25rem',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                width: '100%',
                                left: 0,
                                top: '100%',
                              }}
                            >
                              {availableBulkProducts.slice(0, 10).map((bp) => (
                                <div
                                  key={bp.id}
                                  onClick={() => {
                                    setSelectedBulkProduct(bp.id);
                                    setBulkProductSearch(`${bp.name} (${bp.sku_number})`);
                                  }}
                                  style={{
                                    padding: '0.75rem',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f1f3f4',
                                    fontSize: '0.875rem',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                  }}
                                >
                                  <div style={{ fontWeight: '500' }}>{bp.name}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                    SKU: {bp.sku_number}
                                    {bp.vendor && ` • ${bp.vendor}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div
                              style={{
                                position: 'absolute',
                                zIndex: 1000,
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                marginTop: '0.25rem',
                                padding: '0.75rem',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                width: '100%',
                                left: 0,
                                top: '100%',
                                fontSize: '0.875rem',
                                color: '#666',
                              }}
                            >
                              {availableBulkProducts.length === 0
                                ? 'Loading bulk products...'
                                : 'No bulk products found'}
                            </div>
                          ))}
                      </div>
                      <div>
                        <label
                          htmlFor="bulk_quantity"
                          style={{
                            fontSize: '0.875rem',
                            marginBottom: '0.25rem',
                            display: 'block',
                          }}
                        >
                          Bulk Qty
                        </label>
                        <input
                          type="number"
                          id="bulk_quantity"
                          value={bulkQuantity}
                          onChange={(e) => setBulkQuantity(parseFloat(e.target.value) || 1)}
                          min="0.01"
                          step="0.01"
                          style={{
                            padding: '0.75rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            width: '80px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="finished_quantity"
                          style={{
                            fontSize: '0.875rem',
                            marginBottom: '0.25rem',
                            display: 'block',
                          }}
                        >
                          Finished Qty
                        </label>
                        <input
                          type="number"
                          id="finished_quantity"
                          value={finishedQuantity}
                          onChange={(e) => setFinishedQuantity(parseFloat(e.target.value) || 1)}
                          min="0.01"
                          step="0.01"
                          style={{
                            padding: '0.75rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            width: '80px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAddBulkRequirement}
                        disabled={
                          !selectedBulkProduct || bulkQuantity <= 0 || finishedQuantity <= 0
                        }
                        style={{
                          padding: '0.75rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor:
                            selectedBulkProduct && bulkQuantity > 0 && finishedQuantity > 0
                              ? '#3b82f6'
                              : '#e5e7eb',
                          color:
                            selectedBulkProduct && bulkQuantity > 0 && finishedQuantity > 0
                              ? 'white'
                              : '#9ca3af',
                          border: 'none',
                          borderRadius: '4px',
                          cursor:
                            selectedBulkProduct && bulkQuantity > 0 && finishedQuantity > 0
                              ? 'pointer'
                              : 'not-allowed',
                          fontWeight: '500',
                        }}
                      >
                        Add
                      </button>
                    </div>
                    <small
                      style={{
                        color: '#6c757d',
                        fontSize: '0.875rem',
                        marginTop: '0.5rem',
                        display: 'block',
                      }}
                    >
                      <strong>Ratio:</strong> {bulkQuantity} bulk cases : {finishedQuantity}{' '}
                      finished cases
                      <br />
                      Examples: "1:15" (1 bulk makes 15 finished), "12:1" (12 bulk makes 1 finished)
                    </small>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowForm(false);
                      setEditingProduct(null);
                      resetForm();
                      setModalError('');
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Production Modal */}
      {showConvertModal && convertingProduct && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            if (!convertLoading) {
              setShowConvertModal(false);
              setConvertingProduct(null);
              setConvertError('');
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e1e5e9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Convert to Production Product
              </h2>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: convertLoading ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  if (!convertLoading) {
                    setShowConvertModal(false);
                    setConvertingProduct(null);
                    setConvertError('');
                  }
                }}
                disabled={convertLoading}
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
              <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                Converting <strong>{convertingProduct.name}</strong> to a production product. Please
                provide all required production fields.
              </p>

              {convertError && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    color: '#c33',
                  }}
                >
                  {convertError}
                </div>
              )}

              <form onSubmit={handleConvertSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="convert_shelf_life_days">Shelf Life (days) *</label>
                    <input
                      type="number"
                      id="convert_shelf_life_days"
                      value={convertFormData.shelf_life_days}
                      onChange={(e) =>
                        setConvertFormData({ ...convertFormData, shelf_life_days: e.target.value })
                      }
                      required
                      min="1"
                      disabled={convertLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="convert_storage_requirements">Storage Requirements *</label>
                    <select
                      id="convert_storage_requirements"
                      value={convertFormData.storage_requirements}
                      onChange={(e) =>
                        setConvertFormData({
                          ...convertFormData,
                          storage_requirements: e.target.value as any,
                        })
                      }
                      required
                      disabled={convertLoading}
                    >
                      <option value="Warehouse">Warehouse</option>
                      <option value="Cool Room">Cool Room</option>
                      <option value="Honeyware">Honeyware</option>
                      <option value="Chocolate room">Chocolate room</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="convert_packing_speed_units">Packing Speed Units *</label>
                    <input
                      type="number"
                      id="convert_packing_speed_units"
                      value={convertFormData.packing_speed_units}
                      onChange={(e) =>
                        setConvertFormData({
                          ...convertFormData,
                          packing_speed_units: e.target.value,
                        })
                      }
                      required
                      min="1"
                      disabled={convertLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="convert_packing_speed_time">Packing Speed Time *</label>
                    <input
                      type="number"
                      id="convert_packing_speed_time"
                      value={convertFormData.packing_speed_time}
                      onChange={(e) =>
                        setConvertFormData({
                          ...convertFormData,
                          packing_speed_time: e.target.value,
                        })
                      }
                      required
                      min="0.01"
                      step="0.01"
                      disabled={convertLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="convert_packing_speed_unit">Packing Speed Unit *</label>
                    <select
                      id="convert_packing_speed_unit"
                      value={convertFormData.packing_speed_unit}
                      onChange={(e) =>
                        setConvertFormData({
                          ...convertFormData,
                          packing_speed_unit: e.target.value as any,
                        })
                      }
                      required
                      disabled={convertLoading}
                    >
                      <option value="seconds">Seconds</option>
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="convert_production_line_id">Production Line *</label>
                    <select
                      id="convert_production_line_id"
                      value={convertFormData.production_line_id}
                      onChange={(e) =>
                        setConvertFormData({
                          ...convertFormData,
                          production_line_id: e.target.value,
                        })
                      }
                      required
                      disabled={convertLoading}
                    >
                      <option value="">Select Production Line</option>
                      {productionLines.map((line) => (
                        <option key={line.id} value={line.id}>
                          {line.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="convert_units_per_case">Units per Case</label>
                    <input
                      type="number"
                      id="convert_units_per_case"
                      value={convertFormData.units_per_case}
                      onChange={(e) =>
                        setConvertFormData({
                          ...convertFormData,
                          units_per_case: e.target.value,
                        })
                      }
                      min="1"
                      disabled={convertLoading}
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '1.5rem',
                    paddingTop: '1.5rem',
                    borderTop: '1px solid #e1e5e9',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.75rem',
                  }}
                >
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      if (!convertLoading) {
                        setShowConvertModal(false);
                        setConvertingProduct(null);
                        setConvertError('');
                      }
                    }}
                    disabled={convertLoading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={convertLoading}>
                    {convertLoading ? 'Converting...' : 'Convert to Production'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
